import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();

  const navItems = [
    { path: '/', label: t.nav.home },
    { path: '/about', label: 'Notre Maison' },
    { path: '/books', label: 'Livres' },
    { path: '/news', label: 'Actualités' },
    { path: '/videos', label: 'Interviews' },
    { path: '/authors', label: 'Devenir auteur' },
    { path: '/contact', label: t.nav.contact }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-hermes-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-3xl font-serif font-bold text-stone-900">Quid Novi</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap px-2 ${
                  isActive(item.path) 
                    ? 'text-hermes-600 border-b-2 border-hermes-600 pb-1' 
                    : 'text-stone-700 hover:text-hermes-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 border-l border-hermes-300 pl-4 ml-2">
              <Globe className="w-4 h-4 text-stone-600" />
              <button
                onClick={() => changeLanguage('fr')}
                className={`text-sm font-medium transition-colors ${
                  language === 'fr' ? 'text-hermes-600' : 'text-stone-500 hover:text-hermes-600'
                }`}
              >
                FR
              </button>
              <span className="text-stone-400">|</span>
              <button
                onClick={() => changeLanguage('en')}
                className={`text-sm font-medium transition-colors ${
                  language === 'en' ? 'text-hermes-600' : 'text-stone-500 hover:text-hermes-600'
                }`}
              >
                EN
              </button>
            </div>

            <Link
              to="/submission"
              className="px-4 py-2 bg-hermes-600 text-white text-sm font-medium rounded-none hover:bg-hermes-700 transition-colors ml-4 whitespace-nowrap"
            >
              Soumettre
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-stone-700 hover:text-hermes-600 transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-hermes-200">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-none text-base font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-hermes-600 bg-hermes-50'
                    : 'text-stone-700 hover:text-hermes-600 hover:bg-stone-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            <div className="flex items-center space-x-3 px-3 py-3 border-t border-hermes-200 mt-2 pt-4">
              <Globe className="w-4 h-4 text-stone-600" />
              <button
                onClick={() => {
                  changeLanguage('fr');
                  setIsOpen(false);
                }}
                className={`text-sm font-medium ${
                  language === 'fr' ? 'text-hermes-600' : 'text-stone-600'
                }`}
              >
                Français
              </button>
              <span className="text-stone-400">|</span>
              <button
                onClick={() => {
                  changeLanguage('en');
                  setIsOpen(false);
                }}
                className={`text-sm font-medium ${
                  language === 'en' ? 'text-hermes-600' : 'text-stone-600'
                }`}
              >
                English
              </button>
            </div>

            <Link
              to="/submission"
              onClick={() => setIsOpen(false)}
              className="block w-full mt-2 px-4 py-3 bg-hermes-600 text-white text-center text-sm font-medium rounded-none hover:bg-hermes-700 transition-colors"
            >
              Soumettre un manuscrit
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
